import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ConfigProvider } from 'antd';
import PropTypes from 'prop-types';
import i18n from '../i18n';
import { SettingsProvider } from '../Context/SettingsContext';

const brandPrimary = '#067C60';
const brandPrimaryHover = '#078A74';
const deepText = '#2E405EE0';
const mutedText = '#5C6475';
const softSurface = 'white';
const cardSurface = '#FFFFFF';
const borderBase = '#EBEBEB';
const controlHeight = 42;
const controlRadius = 12;

const componentProps = {
  menu: {
    style: { paddingLeft: 0 },
  },
  select: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      background: softSurface,
    },
  },
  treeSelect: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      background: softSurface,
    },
  },
  datePicker: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      background: softSurface,
    },
  },
  input: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      background: softSurface,
    },
  },
  rangePicker: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      background: softSurface,
    },
  },
  button: {
    style: {
      height: `${controlHeight}px`,
      borderRadius: controlRadius,
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    },
  },
  card: {
    style: {
      boxShadow: '0 12px 38px rgba(15, 23, 42, 0.08)',
      borderRadius: controlRadius + 2,
      borderColor: borderBase,
      borderWidth: 1,
      borderStyle: 'solid',
    },
  },
  modal: {
    styles: {
      body: { paddingTop: 12 },
      wrapper: { scrollbarWidth: 1 },
      header: {
        borderLeft: `4px solid ${brandPrimary}`,
        borderRadius: 0,
        paddingInlineStart: 12,
        fontSize: 18,
      },
    },
  },
};

const themeConfig = {
  cssVars: true,
  token: {
    fontFamily: "'Plus Jakarta Sans','Inter','Public Sans',sans-serif",
    colorPrimary: brandPrimary,
    colorPrimaryHover: brandPrimaryHover,
    colorInfo: brandPrimary,
    colorSuccess: '#0EA76F',
    colorWarning: '#F59E0B',
    colorError: '#F04438',
    colorText: deepText,
    colorTextBase: deepText,
    colorTextSecondary: mutedText,
    colorTextDescription: '#6B7280',
    colorTextHeading: deepText,
    colorLink: brandPrimary,
    colorLinkHover: brandPrimaryHover,
    colorBgLayout: softSurface,
    colorBgBase: softSurface,
    colorBgContainer: cardSurface,
    colorBgElevated: cardSurface,
    colorBorder: borderBase,
    colorBorderSecondary: '#F2F2F2',
    borderRadius: controlRadius,
    controlHeight: controlHeight,
    boxShadow: '0 18px 70px rgba(15, 23, 42, 0.08)',
    boxShadowSecondary: '0 10px 40px rgba(15, 23, 42, 0.06)',
    fontSize: 13,
    fontSizeHeading3: 21,
    lineHeight: 1.55,
  },
  components: {
    Button: {
      borderRadius: controlRadius,
      controlHeight,
      controlOutline: 'rgba(15, 191, 159, 0.35)',
      defaultShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
      primaryShadow: '0 12px 32px rgba(6, 124, 96, 0.24)',
      linkHoverBg: 'rgba(15, 191, 159, 0.08)',
      defaultHoverBg: 'rgba(6, 124, 96, 0.06)',
      defaultActiveBg: 'rgba(6, 124, 96, 0.1)',
    },
    Select: {
      controlHeight,
      borderRadius: controlRadius,
      colorBorder: borderBase,
      colorBgContainer: softSurface,
      optionSelectedBg: 'rgba(15, 191, 159, 0.14)',
      optionActiveBg: 'rgba(15, 191, 159, 0.08)',
    },
    Tooltip: {
      controlHeight: 32,
    },
    Descriptions: {
      labelColor: mutedText,
      colorText: deepText,
      colorTextSecondary: mutedText,
      colorBorder: borderBase,
      colorBgContainer: cardSurface,
      
    },
    Spin: {
      size: 'small',
    },
    Progress: {
      circleTextFontSize: 9,
      lineBorderRadius: 999,
      stepsItemBg: 'rgba(15, 191, 159, 0.14)',
    },
    DatePicker: {
      controlHeight,
      borderRadius: controlRadius,
      colorBorder: borderBase,
      colorBgContainer: softSurface,
      controlItemBgHover: 'rgba(15, 191, 159, 0.08)',
    },
    Table: {
      headerBg: '#F3F3F3',
      headerColor: '#6C6C6C',
      colorBgContainer: cardSurface,
      rowHoverBg: cardSurface,
      borderColor: '#EDF1F7',
      headerBorderRadius: controlRadius,
    },
    Input: {
      controlHeight,
      borderRadius: controlRadius,
      activeShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
      colorBgContainer: softSurface,
      colorBorder: borderBase,
      fontSize: 13,
      paddingBlock: 6,
      color: deepText,
    },
    InputNumber: {
      controlHeight,
      borderRadius: controlRadius,
      activeShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
      colorBgContainer: softSurface,
      colorBorder: borderBase,
      fontSize: 13,
      paddingBlock: 6,
      color: deepText,
    },
    Menu: {
      itemHeight: 44,
      radiusItem: 10,
      colorItemTextSelected: brandPrimary,
      colorItemTextHover: deepText,
      colorItemBgSelected: 'rgba(15, 191, 159, 0.14)',
      colorItemBgHover: 'rgba(15, 191, 159, 0.08)',
      iconSize: 22,
      collapsedIconSize: 22,
    },
    Tag: {
      colorBgElevated: cardSurface,
      colorBgBase: cardSurface,
      colorBorder: borderBase,
      colorText: deepText,
      colorTextBase: deepText,
      colorTextSecondary: mutedText,
      colorTextDescription: '#6B7280',
    },
    Modal: {
      borderRadiusLG: 16,
      colorBgElevated: cardSurface,
      headerBg: cardSurface,
      titleFontSize: 18,
      titleColor: deepText,
      boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
    },
    Card: {
      borderRadius: controlRadius + 2,
      borderRadiusLG: 16,
      borderColor: borderBase,
      boxShadow: '0 12px 36px rgba(15, 23, 42, 0.06)',
      colorBgContainer: cardSurface,
      headerFontSize: 14,
      headerHeight: 52,
      hoverBoxShadow: '0 16px 48px rgba(15, 23, 42, 0.09)',
    },
    Layout: {
      bodyBg: softSurface,
      headerBg: cardSurface,
      siderBg: deepText,
      headerHeight:68,
    },
    Tabs: {
      itemColor: mutedText,
      itemSelectedColor: deepText,
      inkBarColor: brandPrimary,
      inkBarHeight: 4,
      itemHoverColor: deepText,
      itemHoverBg: 'rgba(6, 124, 96, 0.05)',
    },
  },
};

export default function Providers({ children }) {
  const queryClient = new QueryClient();

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <ConfigProvider
            locale={{
              Table: { emptyText: i18n.t('common:noData') },
            }}
            {...componentProps}
            theme={themeConfig}
          >
            {children}
          </ConfigProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

Providers.propTypes = {
  children: PropTypes.node.isRequired,
};
